// The structured outcome of a committed queued-edits review — the durable
// summary an AI agent (or any caller) reads back to learn what the human
// actually decided once a proposal Bundle reaches its terminal state. Generic
// across every queued-edits tool: built from the `ResourceChangeSet[]` the
// review already computed plus the set of approved change ids, so a tag edit, a
// coverage update, or any future proposal yields the same shape.
//
// This is intentionally NOT the apply bundle and NOT the FHIR write — it carries
// only the human-meaningful intent of each entry (its label + proposer intent +
// mutation verb) and whether the reviewer approved or rejected it, so a model
// reasoning over it can say "you approved removing the Onsite tag" rather than
// re-proposing a change that was already decided.

import type { Bundle } from '@medplum/fhirtypes'
import type { ProposalSource } from '#src/models/audit/QueuedEditsProposalAuditEvent.ts'
import type { ResourceChangeSet } from '#src/queued-edits/model.ts'

export type ProposalOutcome =
  // Every proposed entry approved and applied.
  | 'applied'
  // Every proposed entry rejected; nothing written.
  | 'rejected'
  // A mix — some approved (and applied), some rejected.
  | 'partially-applied'

// One reviewed entry's human-meaningful outcome.
export interface CommittedEntryOutcome {
  // The entry's user-facing card title, e.g. "Onsite tag", "Coverage".
  label: string
  // The proposer-supplied plain-English intent, when present, e.g.
  // "Remove the Onsite tag".
  intent: string | undefined
  // The mutation kind this entry performed.
  verb: ResourceChangeSet['verb']
  // `ResourceType/id` of the affected resource. For approved creates (POST) this
  // is the SERVER-ASSIGNED reference resolved from the apply response — the only
  // place a newly-created resource's id exists — so a later agent turn can read
  // or act on the resource it just created. For updates/deletes it's the
  // existing resource's reference; undefined when not resolvable (a rejected
  // create never executed, so it has no id).
  reference: string | undefined
  decision: 'approved' | 'rejected'
}

// The full committed-review payload an agent reads back as a tool result. The
// enclosing `QueuedEditResult` already discriminates on `status: 'committed'`,
// so this payload carries only `outcome` — no redundant inner status field.
export interface ProposalCommitOutcome {
  outcome: ProposalOutcome
  approvedCount: number
  rejectedCount: number
  entries: CommittedEntryOutcome[]
}

// The lifecycle result a queued-edit proposal tool resolves with — generic
// across use cases (a tag edit today, any future proposal). The inline tool
// stages a Bundle, the human reviews it, and the chat re-submits the terminal
// decision as the tool output so a later agent turn knows what was decided.
//   staged    — persisted, awaiting the human's review (carries the Bundle ref)
//   committed — the human applied; carries the per-entry decision outcome
//   noop      — a well-formed request that changes nothing
//   error     — the proposal could not be built/persisted
//
// A handler that builds a proposal resolves with the staged-time subset
// (staged/noop/error); `committed` is produced later by the review surface.
export type QueuedEditResult =
  | { status: 'staged'; bundleReference: string }
  | {
      status: 'committed'
      bundleReference: string
      outcome: ProposalCommitOutcome
    }
  | { status: 'noop'; reason: string }
  | { status: 'error'; reason: string }

// The subset a proposal-building handler can resolve with — before any human
// review, so never `committed`.
export type QueuedEditStagedResult = Exclude<
  QueuedEditResult,
  { status: 'committed' }
>

// A queued-edit client tool's async fulfillment: build + persist the proposal
// Bundle from the model's tool input and resolve with the staged-time result.
// Identical across queued-edit tools, so the dispatch path is generic over it.
// `source` is forwarded into the persisting transaction so the proposal's
// provenance lands with it, never as a follow-up write.
export type QueuedEditHandler = (args: {
  toolCallId: string
  input: unknown
  source?: ProposalSource
}) => Promise<QueuedEditStagedResult>

interface AppliedReferences {
  [fullUrl: string]: string
}

// Map each applied entry's `fullUrl` to the server-resolved `ResourceType/id`,
// read from the apply response's `entry[i].response.location`. The request and
// response bundles align by index (FHIR batch/transaction contract), so the
// request entry's `fullUrl` keys the response location. This is the only place a
// newly-created (POST) resource's server-assigned id surfaces — at propose time
// the entry has just a `urn:uuid:` fullUrl and no id. History suffixes
// (`/_history/{vid}`) are stripped to the bare `ResourceType/id`. Entries with no
// location (a no-op patch the server dropped) contribute nothing.
export function appliedReferencesByFullUrl(
  appliedBundle: Bundle,
  executedBundle: Bundle
): AppliedReferences {
  const map: AppliedReferences = {}
  const requestEntries = appliedBundle.entry ?? []
  const responseEntries = executedBundle.entry ?? []
  for (const [index, request] of requestEntries.entries()) {
    const location = responseEntries[index]?.response?.location
    if (request.fullUrl && location) {
      map[request.fullUrl] = location.split('/_history/')[0] ?? location
    }
  }
  return map
}

// Build the committed-review outcome from the reviewed change sets and the ids
// the reviewer approved (everything else is treated as rejected). The single
// source of the model-readable decision payload — both the inline review card
// and any future caller produce it the same way. `appliedReferences` (optional)
// maps an applied entry's `fullUrl` to its server-resolved `ResourceType/id`,
// resolved from the apply response — pass it so approved creates carry the
// newly-created resource's reference (otherwise a POST entry has no id to
// report). It only ever supersedes a propose-time reference for approved
// entries; rejected ones never executed.
export function buildProposalCommitOutcome(
  changes: ResourceChangeSet[],
  approvedIds: string[],
  appliedReferences: Record<string, string> = {}
): ProposalCommitOutcome {
  const approved = new Set(approvedIds)
  const entries: CommittedEntryOutcome[] = changes.map((change) => {
    const isApproved = approved.has(change.id)
    const resolved =
      isApproved && change.fullUrl
        ? appliedReferences[change.fullUrl]
        : undefined
    return {
      label: change.label,
      intent: change.intent,
      verb: change.verb,
      reference: resolved ?? change.reference,
      decision: isApproved ? 'approved' : 'rejected',
    }
  })
  const approvedCount = entries.filter((e) => e.decision === 'approved').length
  const rejectedCount = entries.length - approvedCount

  let outcome: ProposalOutcome
  if (approvedCount === 0) {
    outcome = 'rejected'
  } else if (rejectedCount === 0) {
    outcome = 'applied'
  } else {
    outcome = 'partially-applied'
  }

  return {
    outcome,
    approvedCount,
    rejectedCount,
    entries,
  }
}
